/**
 * F18.9 — UI di gestione tour onboarding (montato in /profile).
 *
 * Per ogni tour visibile al ruolo dell'utente: card con titolo, descrizione,
 * durata stimata, status badge, e azioni (Avvia / Riavvia / Salta).
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, RefreshCw, X, Check, Eye, Smartphone } from 'lucide-react';
import { useTours } from './useTours';
import type { AvailableTour } from './useTours';

const TourSelector: React.FC = () => {
  const { availableTours, startTour, dismissTour, resetTour, isMobile } = useTours();

  if (availableTours.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tour & Tutorial</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Nessun tour disponibile per il tuo ruolo.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Play className="h-4 w-4 text-primary" />
          Tour & Tutorial
        </CardTitle>
        <CardDescription>
          Avvia un tour guidato per esplorare DryConfig. Puoi rivederli quando vuoi.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {isMobile && (
          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
            <Smartphone className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              I tour guidati sono disponibili solo da <strong>desktop</strong> (schermo ≥ 768px).
              Apri DryConfig da un computer per esplorarli.
            </span>
          </div>
        )}
        {availableTours.map((tour) => (
          <TourCard
            key={tour.meta.id}
            tour={tour}
            onStart={() => startTour(tour.meta.id)}
            onDismiss={() => dismissTour(tour.meta.id)}
            onReset={() => resetTour(tour.meta.id)}
            disabled={isMobile}
          />
        ))}
      </CardContent>
    </Card>
  );
};

interface TourCardProps {
  tour: AvailableTour;
  onStart: () => void;
  onDismiss: () => void;
  onReset: () => void;
  disabled?: boolean;
}

const TourCard: React.FC<TourCardProps> = ({ tour, onStart, onDismiss, onReset, disabled }) => {
  const { meta, mode, status } = tour;
  const Icon = meta.icon;

  return (
    <div className="flex items-start gap-3 p-3 rounded-md border bg-white">
      <div className="shrink-0 w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-zinc-900 truncate">{meta.title}</span>
          {mode === 'readonly' && (
            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-800 border-amber-200 gap-1">
              <Eye className="h-2.5 w-2.5" />
              Sola lettura
            </Badge>
          )}
          {status === 'completed' && (
            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-800 border-emerald-200 gap-1">
              <Check className="h-2.5 w-2.5" />
              Completato
            </Badge>
          )}
          {status === 'dismissed' && (
            <Badge variant="outline" className="text-[9px] bg-zinc-100 text-zinc-700 border-zinc-300">
              Saltato
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
        <p className="text-[10px] text-muted-foreground/70 mt-0.5">
          ≈ {meta.estimatedMinutes < 1 ? '30 sec' : `${meta.estimatedMinutes} min`}
        </p>
      </div>
      <div className="shrink-0 flex flex-col gap-1.5">
        {status === 'untouched' && (
          <>
            <Button size="sm" onClick={onStart} disabled={disabled} className="h-7 text-xs">
              <Play className="h-3 w-3 mr-1" /> Avvia
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss} disabled={disabled} className="h-6 text-[10px] text-muted-foreground">
              <X className="h-3 w-3 mr-1" /> Salta
            </Button>
          </>
        )}
        {status === 'completed' && (
          <Button size="sm" variant="outline" onClick={onStart} disabled={disabled} className="h-7 text-xs">
            <RefreshCw className="h-3 w-3 mr-1" /> Rivedi
          </Button>
        )}
        {status === 'dismissed' && (
          <Button size="sm" variant="outline" onClick={onReset} disabled={disabled} className="h-7 text-xs">
            <RefreshCw className="h-3 w-3 mr-1" /> Ripristina
          </Button>
        )}
      </div>
    </div>
  );
};

export default TourSelector;
